import React, { useRef, useState, useEffect } from 'react';

import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetSlot, SheetFooter, SheetClose } from "@/components/templates/sheet";
import { Button } from '@/components/molecules/buttons/button';
import { AddRounded, DirectionsCar } from '@mui/icons-material';

// import FormNewPayslip from '@/components/macro-organisms/panels/panels-admin/newPayslip/FormNewPayslip';
import Action from '@/components/molecules/buttons/Action';
import FormNewPayslip from './FormNewPayslip';
import { useStorePayslip } from '@/services/admin-services/api-rest/useStorePayslip';
import { useAddPayslip } from '@/services/admin-services/mutations/useAddPayslip';
import PrintErrorMessage from '@/components/organisms/feedback/PrintErrorMessage';
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import FormPayslipBook from './FormPayslipBook';
import Payslip from '@/components/macro-organisms/tabs/consultants-vision-admin/payslib/payslip';
import { useSimulatePayslipImport } from '@/services/admin-services/api-rest/useSimulatePayslipImport';
import { Label } from '@/components/atoms/texts/label';
import ListOfPayslips from './ListOfPayslips';
import useConsultant from '@/components/organisms/navigation/MenuConsultant/useConsultant';

export default function ImportPayslipBookPanel({ global = true, refetchFromScratch = () => { } }) {
    const submitRef = useRef(null);
    const [openNewPayslip, setOpenNewPayslip] = useState(false);
    const [dataToSend, setDataToSend] = useState();
    const [disableAnalyse, setDisableAnalayse] = useState(true);
    const [submissionError, setSubmissionError] = useState(false);
    const [submissionSuccess, setSubmissionSuccess] = useState(false);
    const { simulatePayslipImport, loadingPayslipBookImport, errorPayslipBookImport } = useSimulatePayslipImport();
    const [usersData, setUsersData] = useState([]);
    const [usersDataCopy, setUsersDataCopy] = useState([]); // Copie des données pour affichage
    const { addPayslip, loading: loadingAdd } = useAddPayslip();
    const [unknownPayslips, setUnknownPayslips] = useState([]); // Ajout de l'état pour les unknownPayslips
    const { usersFormated } = useConsultant();

    const getMonthAndYearFromPeriod = (period) => {
        if (!period) {
            return { month: null, year: null };
        }

        if (typeof period === "string") {
            const match = period.match(/^(\d{4})-(\d{2})(?:-\d{2})?$/);
            if (match) {
                return {
                    year: Number(match[1]),
                    month: Number(match[2]),
                };
            }
        }

        const periodDate = new Date(period);
        if (Number.isNaN(periodDate.getTime())) {
            return { month: null, year: null };
        }

        return {
            month: periodDate.getMonth() + 1,
            year: periodDate.getFullYear(),
        };
    };

    const normalizePayslipForDisplay = (payslip, fallbackUuid) => {
        const { month, year } = getMonthAndYearFromPeriod(payslip?.period);

        return {
            ...payslip,
            uuid: payslip?.uuid || fallbackUuid,
            month,
            year,
        };
    };

    const handleSubmit = async (formData) => {
        try {
            console.log("Submitting form data:", formData);
            setSubmissionError(null); // Réinitialise l'erreur avant la soumission
            setSubmissionSuccess(false);

            console.log("Calling simulatePayslipImport...");
            const { data, error } = await simulatePayslipImport(formData.payslipFile, formData.paymentDate);

            if (error) {
                console.error("Error from simulatePayslipImport:", error);
                throw new Error(error);
            }

            console.log("Data received from simulatePayslipImport:", data);
            setUsersData(data.users); // Mise à jour des utilisateurs

            // Ajout d'IDs uniques aux payslips inconnus
            let currentId = 1;
            const unknownPayslipsWithIds = data.unknownPayslips.map((payslip) =>
                normalizePayslipForDisplay(payslip, currentId++)
            );
            setUnknownPayslips(unknownPayslipsWithIds); // Mise à jour des unknownPayslips

            // Transformation pour ajouter des ids séquentiels, mois et année dans usersDataCopy
            const dataWithIds = data.users.map((user) => ({
                ...user,
                payslips: user.payslips.map((payslip) => normalizePayslipForDisplay(payslip, currentId++)),
            }));

            setUsersDataCopy(dataWithIds); // Copie transformée pour affichage
            setSubmissionSuccess(true);
        } catch (err) {
            console.error("Error during handleSubmit:", err.message);
            setSubmissionError(err.message); // Déclenche la réinitialisation du formulaire
        } finally {
            console.log("handleSubmit process completed.");
        }
    };

    const handleClose = (close) => {
        setSubmissionError(null);
        setSubmissionSuccess(null);
        setUsersData([]); // Réinitialise les données des utilisateurs
        setUsersDataCopy([]); // Réinitialise la copie des données
        setDataToSend(null); // Réinitialise les données à envoyer
        setUnknownPayslips([]); // Réinitialise les unknownPayslips
        // submitRef.current && submitRef.current?.reset(); // Réinitialise le formulaire si possible
        setOpenNewPayslip(close); // Ferme ou ouvre le panneau
    };

    const savePayslips = async () => {
        try {
            setSubmissionError(null);
            setSubmissionSuccess(false);

            const updatedUsersDataCopy = [...usersDataCopy];
            let hasSaveErrors = false;

            for (const user of usersDataCopy) {
                for (const payslip of user.payslips) {
                    const newPayslip = {
                        payslipFileUuid: null,
                        consultantUuid: user.uuid,
                        period: payslip.period,
                        currency: payslip.currency,
                        grossSalary: parseFloat(payslip.grossSalary),
                        netSalary: parseFloat(payslip.netSalary),
                        taxes: parseFloat(payslip.taxes),
                        employerContributions: parseFloat(payslip.employerContributions),
                        employeeContributions: parseFloat(payslip.employeeContributions),
                        comment: payslip.comment,
                        bonus: payslip.bonus,
                        paymentDate: payslip.paymentDate,
                    };

                    console.log("Saving payslip:", newPayslip);

                    try {
                        await addPayslip({
                            variables: newPayslip,
                        });

                        // Retirer la payslip ajoutée avec succès de usersDataCopy
                        const userIndex = updatedUsersDataCopy.findIndex((u) => u.uuid === user.uuid);
                        if (userIndex !== -1) {
                            updatedUsersDataCopy[userIndex].payslips = updatedUsersDataCopy[userIndex].payslips.filter(
                                (p) => p.uuid !== payslip.uuid
                            );

                            // Si l'utilisateur n'a plus de payslips, le retirer de la liste
                            if (updatedUsersDataCopy[userIndex].payslips.length === 0) {
                                updatedUsersDataCopy.splice(userIndex, 1);
                            }
                        }
                    } catch (err) {
                        console.error("Error saving payslip:", err.message);
                        hasSaveErrors = true;
                        setSubmissionError(err);
                        continue; // Skip to the next payslip
                    }
                }
            }

            setUsersDataCopy(updatedUsersDataCopy); // Mettre à jour la copie des données
            setSubmissionSuccess(!hasSaveErrors);

            if (!hasSaveErrors) {
                refetchFromScratch();
            }

            // Ferme le panel si toutes les payslips ont été envoyées.
            if (!hasSaveErrors && updatedUsersDataCopy.length === 0 && unknownPayslips.length === 0) {
                handleClose(false);
            }
        } catch (err) {
            console.error("Error during savePayslips:", err.message);
            setSubmissionError(err);
        }
    };

    const setPayslip = (updatedPayslip) => {
        console.log("Updating payslip:", updatedPayslip);

        // Fonction utilitaire pour remplir month et year
        const fillMonthAndYear = (payslip) => {
            const { month, year } = getMonthAndYearFromPeriod(payslip?.period);
            return {
                ...payslip,
                month,
                year,
            };
        };

        // Supprimer la payslip de l'ancien utilisateur si le consultantUuid a changé
        const updatedUsersDataCopy = usersDataCopy
            ?.map((user) => {
                if (user.payslips.some((payslip) => payslip.uuid === updatedPayslip.uuid)) {
                    // Si le consultantUuid a changé, supprimer la payslip de cet utilisateur
                    if (user.uuid !== updatedPayslip.consultantUuid) {
                        return {
                            ...user,
                            payslips: user.payslips.filter((payslip) => payslip.uuid !== updatedPayslip.uuid),
                        };
                    }
                }
                return user;
            })
            ?.filter((user) => user.payslips.length > 0); // Supprime les utilisateurs sans payslips

        // Ajouter ou mettre à jour la payslip dans le nouvel utilisateur
        if (updatedPayslip?.consultantUuid) {
            let targetUser = updatedUsersDataCopy?.find((user) => user?.uuid === updatedPayslip?.consultantUuid);

            if (!targetUser) {
                const userInfo = usersFormated.find((user) => user?.uuid === updatedPayslip?.consultantUuid) || {};
                targetUser = {
                    uuid: updatedPayslip?.consultantUuid,
                    firstname: userInfo?.firstname || "",
                    lastname: userInfo?.lastname || "",
                    trigram: userInfo?.trigram || "",
                    payslips: [],
                };
                updatedUsersDataCopy?.push(targetUser);
            }

            let currentId = 1000; // Déclaration de currentId pour les IDs séquentiels
            const updatedEntry = {
                ...fillMonthAndYear(updatedPayslip),
                uuid: updatedPayslip?.uuid || currentId++,
            };
            const existingIndex = targetUser?.payslips?.findIndex((p) => p.uuid === updatedPayslip?.uuid);
            if (existingIndex !== undefined && existingIndex >= 0) {
                targetUser.payslips[existingIndex] = updatedEntry; // Écrase l'ancienne
            } else {
                targetUser?.payslips?.push(updatedEntry); // Nouvelle entrée
            }

            // Retirer la payslip de unknownPayslips si elle y est présente
            setUnknownPayslips((prevUnknownPayslips) =>
                prevUnknownPayslips?.filter((payslip) => payslip?.uuid !== updatedPayslip?.uuid)
            );
        } else {
            // Si aucun consultant n'est sélectionné, mettre à jour la payslip dans unknownPayslips
            setUnknownPayslips((prevUnknownPayslips) =>
                prevUnknownPayslips.map((payslip) =>
                    payslip.uuid === updatedPayslip?.uuid
                        ? { ...payslip, ...fillMonthAndYear(updatedPayslip) }
                        : payslip
                )
            );
        }

        console.log("Updated usersDataCopy:", updatedUsersDataCopy);
        setUsersDataCopy(updatedUsersDataCopy); // Met à jour l'état avec une copie immuable
    };

    const removePayslip = (payslipToRemove) => {
        console.log("Removing payslip:", payslipToRemove);

        // Supprimer la payslip de usersDataCopy si elle y appartient
        const updatedUsersDataCopy = usersDataCopy
            ?.map((user) => {
                if (user?.payslips?.some((payslip) => payslip?.uuid === payslipToRemove?.uuid)) {
                    console.log("Removing payslip from user:", user.uuid);
                    return {
                        ...user,
                        payslips: user?.payslips?.filter((payslip) => payslip?.uuid !== payslipToRemove?.uuid),
                    };
                }
                return user;
            })
            ?.filter((user) => user.payslips.length > 0); // Supprime les utilisateurs sans payslips

        setUsersDataCopy(updatedUsersDataCopy);

        // Supprimer la payslip de unknownPayslips si elle y appartient
        const updatedUnknownPayslips = unknownPayslips?.filter(
            (payslip) => payslip?.uuid !== payslipToRemove.uuid
        );

        setUnknownPayslips(updatedUnknownPayslips);

        console.log("Updated usersDataCopy:", updatedUsersDataCopy);
        console.log("Updated unknownPayslips:", updatedUnknownPayslips);
    };

    useEffect(() => {
        usersDataCopy && console.log("usersDataCopy", usersDataCopy);
    }, [usersDataCopy]);

    return (
        <Sheet open={openNewPayslip} onOpenChange={handleClose}>
            <SheetTrigger asChild className={global ? `w-full` : undefined}>

                {global ? (
                    <Action

                        icon={<RequestQuoteIcon />}
                        text="consultants.dashboard.actions.payslipBook"
                        initialVariant={openNewPayslip ? "active" : "default"}

                    />
                ) : (
                    <Button leftIcon={<AddRounded />} showLeftIcon text="consultants.payslip.addPayslip" />
                )}
            </SheetTrigger>
            <SheetContent>
                <SheetHeader
                    title={"consultants.payslip.addPayslipBook"}
                    subheader={"global.information.allFieldsMandatory"}
                />
                <SheetSlot className="flex flex-col relative ">

                    {(usersDataCopy.length <= 0 && unknownPayslips.length<=0 ) ? (
                        <FormPayslipBook onSubmit={handleSubmit} submitRef={submitRef} setDisableAnalayse={setDisableAnalayse} submissionError={submissionError} />
                    ) : (
                        <ListOfPayslips usersDataCopy={usersDataCopy} unknownPayslips={unknownPayslips} submissionError={submissionError} setPayslip={setPayslip} removePayslip={removePayslip} />
                    )}

                    {/* Affichage des états de chargement et d'erreur */}
                    {loadingPayslipBookImport && <span className='loader' />}
                    {loadingAdd && <span className='loader' />}
                    {/* {submissionError && <PrintErrorMessage error={submissionError} />} */}

                </SheetSlot>
                <SheetFooter
                    buttonGhostText={"global.actions.clear"}
                    buttonOutlineText={(usersDataCopy.length <= 0 && unknownPayslips.length<=0 ) && "global.actions.analyse"}
                    buttonDressedText={usersDataCopy.length > 0 && "global.actions.save" }
                    warning={"global.userBeNotified"}
                    onClickGhost={(e) => handleClose(true)}
                    onClickOutline={(e)=> submitRef.current && submitRef.current.click()}
                    onClickDressed={() => savePayslips()}
                    disabledOutline={disableAnalyse}
                    disabledDressed={loadingAdd || loadingPayslipBookImport || usersDataCopy?.length <= 0 || unknownPayslips?.length > 0}
                />
            </SheetContent>
        </Sheet >
    )
}
